import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { IExercise } from '../../../../core/models/exercise';
import {
  finishPracticeEnemExam,
  getPracticeEnemExam,
  savePracticeEnemExamAnswer,
  startPracticeEnemExam,
} from '../../queries/Enem';
import {
  IPracticeEnem,
  PracticeEnemDay,
  PracticeEnemForeignLanguage,
} from '../../model/Enem';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { parseDayParam, practiceEnemPaths } from '../../utils/enemDay';
import { useMostraSeuAprovaTrigger } from '../../../../core/components/organism/MostraSeuAprovaModal/useMostraSeuAprovaTrigger';

/**
 * Tempo restante da prova, em milissegundos.
 *
 * O prazo vem pronto do servidor (`deadlineAt`), medido contra o relógio dele
 * (`currentDate`) para não herdar o desvio do relógio da máquina do aluno.
 *
 * Tentativas legadas não têm deadlineAt — nesse caso caímos no cálculo antigo,
 * derivado da janela do simulado, para não deixar a prova sem cronômetro.
 *
 * Fora do hook porque é pura e porque a memoização que a usa roda durante o
 * render: como const dentro do hook, ficaria na zona morta temporal.
 */
const getExamTimeLeft = (payload: IPracticeEnem) => {
  const { deadlineAt, currentDate, startDate, endDate } = payload.simulated;

  const serverNow = currentDate ? new Date(currentDate).getTime() : Date.now();

  if (deadlineAt) {
    return Math.max(0, new Date(deadlineAt).getTime() - serverNow);
  }

  const windowDuration =
    new Date(endDate).getTime() - new Date(startDate).getTime();
  const startedAt = new Date(
    payload.history?.startedAt || payload.history?.startDate
  ).getTime();

  if (!Number.isFinite(startedAt) || !Number.isFinite(windowDuration)) {
    return undefined;
  }

  return Math.max(0, startedAt + windowDuration - serverNow);
};

export const usePracticeEnemExamHook = () => {
  const { fire: fireMostraSeuAprova } = useMostraSeuAprovaTrigger();

  const [exercises, setExercises] = useState<IExercise[]>([]);
  const [foreignLanguage, setForeignLanguage] =
    useState<PracticeEnemForeignLanguage>('ingles');

  const { slug, day: dayParam } = useParams();
  const day: PracticeEnemDay = parseDayParam(dayParam);

  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const queryKey = ['fetch-practice-enem-exam', slug, day];

  const { data, isError, error, isSuccess, isPending } = useQuery({
    queryKey,
    queryFn: () => getPracticeEnemExam(slug || '', day),
    refetchOnWindowFocus: false,
    retry: false,
  });

  // Iniciar cria uma tentativa no banco — é mutation, não query. Como useQuery,
  // qualquer refetch da chave geraria uma tentativa nova.
  const { mutateAsync: startExamMutation, isPending: isStarting } = useMutation({
    mutationKey: ['start-practice-enem-exam', slug, day],
    mutationFn: () => startPracticeEnemExam(slug || '', day, foreignLanguage),
    retry: false,
    onSuccess: (started) => {
      queryClient.setQueryData(queryKey, started);
    },
  });

  const { mutateAsync: finishExamMutation, isPending: isFinishing } =
    useMutation({
      mutationKey: ['finish-practice-enem-exam', slug, day],
      mutationFn: (finishedBy: 'usuario' | 'tempo-esgotado') =>
        finishPracticeEnemExam(data?.simulated.id || '', day, finishedBy),
      retry: false,
    });

  useEffect(() => {
    if (isSuccess && data) {
      handleSuccess(data);
    }
    // `data` na dependência, não só `isSuccess`: ao trocar de dia a chave muda e
    // o React Query pode entregar o cache sem passar por pending, deixando a
    // tela com os dados do dia anterior.
  }, [data, isSuccess]);

  // Derivados da resposta, não espelhados em estado.
  //
  // Espelhar `isStarted` num useState criava um limbo: entre o render que
  // recebia os dados e o efeito que sincronizava o estado, a tela não era nem
  // instruções nem prova. Com o prazo já vencido (voltar para a prova depois de
  // finalizar, via "Refazer") esse limbo não saía mais do lugar.
  const showExamInfo = !!data && !data.simulated.isStarted;

  const examTimeLeft = useMemo(
    () => (data?.simulated.isStarted ? getExamTimeLeft(data) : undefined),
    [data]
  );

  const breadCrumbsItems = useMemo(() => {
    const dayLabel = day ? ` — ${day}º dia` : '';

    return [
      {
        label: 'Simuladão Enem',
        href: `/simulados/enem`,
      },
      ...(day
        ? [
            {
              label: data?.simulated.name ?? '',
              href: practiceEnemPaths.daySelection(slug || ''),
            },
          ]
        : []),
      {
        label: showExamInfo
          ? `Instruções para o simulado${dayLabel}`
          : `${data?.simulated.name ?? ''}${dayLabel}`,
        href: '',
      },
    ];
  }, [data, day, showExamInfo, slug]);

  const handleSuccess = (payload: IPracticeEnem) => {
    if (!payload.simulated.isStarted) {
      setExercises([]);
      return;
    }

    setExercises(organizeExercisesFields(payload.simulated.questions, payload));
  };

  const organizeExercisesFields = (
    questions: IExercise[] = [],
    payload: IPracticeEnem
  ) => {
    const answersByQuestion = new Map(
      (payload.history?.answers ?? [])
        .filter((item) => item && item.answer)
        .map((item) => [item.question, item.answer])
    );

    return questions.map((exercise) => {
      let hasAlternativeSelected = false;

      if (exercise.alternatives) {
        const answer = answersByQuestion.get(exercise.questionId) ?? '';

        if (answer) hasAlternativeSelected = true;

        exercise.alternatives = exercise.alternatives.map((alternative) => {
          if (alternative) {
            alternative.id = alternative._id;
            alternative.selected = answer === alternative.alternativeId;
          }

          return alternative;
        });
      }

      exercise.statement = exercise.text;
      exercise.id = exercise._id;
      exercise.hasAlternativeSelected = hasAlternativeSelected;

      return exercise;
    });
  };

  const updateExerciseAnswer = async (exercise: IExercise) => {
    const selected = exercise.alternatives?.find(
      (alternative) => alternative.selected
    );

    if (!selected) return;

    await savePracticeEnemExamAnswer(
      data?.simulated.id || '',
      selected.alternativeId,
      exercise.questionId,
      day
    );
  };

  const updateExercise = async (exercise: IExercise) => {
    const updatedExercises = exercises.map((stateExercise) =>
      stateExercise.id === exercise.id
        ? { ...stateExercise, ...exercise }
        : stateExercise
    );

    setExercises(updatedExercises);

    try {
      await updateExerciseAnswer(exercise);
    } catch (e) {
      // O servidor recusa resposta depois do prazo. Recarregar o estado do dia
      // faz a tela refletir a prova encerrada em vez de seguir aceitando marcações.
      queryClient.invalidateQueries({ queryKey });
    }
  };

  const startExam = async () => {
    await startExamMutation();
  };

  const finishExam = async (isTimeExpired = false) => {
    await finishExamMutation(isTimeExpired ? 'tempo-esgotado' : 'usuario');

    // Descarta o estado da prova encerrada. Sem isso, "Refazer" voltava para
    // esta rota e o React Query servia o payload antigo — prova em andamento
    // com prazo já vencido — antes do refetch corrigir.
    queryClient.removeQueries({ queryKey });

    // Campanha #mostraseuaprova, antes de navegar: o registro começa com a tela
    // ainda montada, e o modal é global, então aparece já sobre o gabarito.
    // `fire` é no-op fora da campanha ou com o aluno inelegível, e nunca lança.
    fireMostraSeuAprova('simulados-ineditos-enem');

    goToFeedbackPage();
  };

  const goToFeedbackPage = () => {
    navigate(practiceEnemPaths.feedback(slug || '', day));
  };

  return {
    data,
    day,
    isError,
    error,
    isSuccess,
    // Esqueleto só enquanto não há resposta para este dia.
    //
    // Antes dependia de `!examTimeLeft`, o que travava a tela em três casos
    // reais: prazo vencido (examTimeLeft === 0, e !0 é true), duração não
    // configurada (undefined) e — o pior — qualquer erro da API, porque a
    // página testa isLoading antes de isError e nunca chegava na tela de erro.
    isLoading: !isError && (isPending || !data),
    isStarting,
    isFinishing,
    exercises,
    examTimeLeft,
    showExamInfo,
    foreignLanguage,
    hasForeignLanguageChoice: day === 1,
    breadCrumbsItems,
    setForeignLanguage,
    updateExercise,
    startExam,
    finishExam,
  };
};
